import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
//slices
import { companySelector } from 'slices/company';
// api
import {
  createHelpText,
  editHelpText,
  deleteHelpText
} from 'api/company';
// components
import CustomInput from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const HelpTextForm = ({ helpTextData, isLoading, isEditing }) => {
  const [localHelpTextData, setLocalHelpTextData] = useState(helpTextData || { text: '', title: '' });
  const [error, setError] = useState({ title_es: '', text_es: '' });
  const navigate = useNavigate();
  const { t } = useTranslation(['backoffice']);
  const [loading, setLoading] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const company = useSelector(companySelector);
  const languages = company.languages;
  const [openAccordion, setOpenAccordion] = useState(
    languages.reduce((acc, language) => ({ ...acc, [language.code_iso2]: false }), {})
  );

  useEffect(() => {
    if (helpTextData) {
      setLocalHelpTextData(helpTextData);
    }
  }, [helpTextData]);

  const handleChange = (name, value) => {
    setLocalHelpTextData(prevData => ({
      ...prevData,
      [name]: value
    }));
    if (value) {
      setError(prevError => ({
        ...prevError,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newError = {};
    let hasError = false;
    languages.forEach(language => {
      const titleKey = `title_${language.code_iso2}`;
      const textKey = `text_${language.code_iso2}`;
      if (!localHelpTextData[titleKey]) {
        newError[titleKey] = t('camporequerido');
        setOpenAccordion(prevOpen => ({
          ...prevOpen,
          [language.code_iso2]: true
        }));
        hasError = true;
      }
      if (!localHelpTextData[textKey]) {
        newError[textKey] = t('camporequerido');
        setOpenAccordion(prevOpen => ({
          ...prevOpen,
          [language.code_iso2]: true
        }));
        hasError = true;
      }
    });
    setError(newError);
    return !hasError;
  };

  const handleResponse = (response, isEditing) => {
    const { status } = response;
    if (status === 200 || status === 201) {
      toast.success(isEditing ? t('editadocorrectamente') : t('creadocorrectamente'));
      navigate('/intranet/help-text');
    } else {
      toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      setLoading(true);
      const dataToSubmit = { ...localHelpTextData };
      if (localHelpTextData.text_es) {
        dataToSubmit.text = localHelpTextData.text_es;
      }
      if (localHelpTextData.title_es) {
        dataToSubmit.title = localHelpTextData.title_es;
      }
      const response = isEditing
        ? await editHelpText(helpTextData.id, dataToSubmit)
        : await createHelpText(dataToSubmit);
      handleResponse(response, isEditing);
    } catch (error) {
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const { status } = await deleteHelpText(localHelpTextData.id);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        navigate('/intranet/help-text', { replace: true });
      } else {
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      console.error('Error deleting help text:', error);
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionToggle = (languageCode) => {
    setOpenAccordion(prevOpen => ({
      ...prevOpen,
      [languageCode]: !prevOpen[languageCode]
    }));
  };

  return (
    <div className='backoffice-accordion mt-5'>
      <Form onSubmit={onSubmit}>
        <div className="card-faqs-container">
          <Row className="mx-1 w-100">
            {languages.map(language => (
              <React.Fragment key={language.code_iso2}>
                <Accordion className="mt-3 card-faqs" activeKey={openAccordion[language.code_iso2] ? "0" : null}>
                  <Accordion.Item eventKey="0" className="bg-transparent border-0">
                    <Accordion.Header
                      className="text-light card-faqs-header mt-0"
                      onClick={() => handleAccordionToggle(language.code_iso2)}
                    >
                      {language.nombre}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="card-faqs-line mb-2" />
                      <div className="d-flex flex-row">
                        <div className="card-faqs-item">
                          <Form.Group className="mb-3">
                            <CustomInput
                              label={`${t('titulo')}`}
                              type="text"
                              name={`title_${language.code_iso2}`}
                              value={localHelpTextData[`title_${language.code_iso2}`] || ''}
                              onChange={(value) => handleChange(`title_${language.code_iso2}`, value)}
                              placeholder={`${t('titulo')}`}
                              required
                            />
                            {error[`title_${language.code_iso2}`] && (
                              <small className="text-danger">{error[`title_${language.code_iso2}`]}</small>
                            )}
                          </Form.Group>
                        </div>
                        <div className="card-faqs-item">
                          <Form.Group className="mb-3">
                            <Form.Label>{t('text')}</Form.Label>
                            {openAccordion[language.code_iso2] && (
                              <CkEditorComponent
                                value={localHelpTextData[`text_${language.code_iso2}`] || ''}
                                onChange={(content) => handleChange(`text_${language.code_iso2}`, content)}
                                placeholder={t('text')}
                              />
                            )}
                            {error[`text_${language.code_iso2}`] && (
                              <small className="text-danger">{error[`text_${language.code_iso2}`]}</small>
                            )}
                          </Form.Group>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </React.Fragment>
            ))}
          </Row>
        </div>
        <Row className="my-4">
          <Col sm={12}>
            <div className="d-flex justify-content-end gap-3">
              <Button
                color="primary"
                type="submit"
                className='btn-intra width-btn-save bg-primary-color btn btn-primary'
                loading={loading}
              >
                {t('save')}
                <SpinnerComponent loading={loading} />
              </Button>
              {isEditing && (
                <Button
                  color="secondary"
                  className='btn-intra bg-secondary-color btn btn-primary' onClick={() => setOpenModalDelete(true)} disabled={loading}
                >
                  {t('eliminar')}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Form>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deleteHelpText')}
        beforeTitle={t('edit', { title: helpTextData?.title })}
      />
    </div>
  );
};

HelpTextForm.propTypes = {
  helpTextData: PropTypes.object,
  isLoading: PropTypes.bool,
  isEditing: PropTypes.bool
};

export default HelpTextForm;
